import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "./ThemeToggle";
import { useContactSettings } from "@/hooks/useContactSettings";
import knmcLogo from "@/assets/knmc-logo.png";

const nav = [
  { label: "Departments", to: "/departments" },
  { label: "Find a Doctor", to: "/doctors" },
  { label: "Health Packages", to: "/health-packages" },
  { label: "Health Articles", to: "/health-articles" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

interface HeaderSettingsData {
  brand_name?: string;
  brand_subtitle?: string;
  book_btn_label?: string;
}

export function Header({
  overlay = false,
  settings,
  phone: phoneProp,
}: {
  overlay?: boolean;
  settings?: HeaderSettingsData | null;
  phone?: string;
}) {
  const { settings: contactSettings } = useContactSettings();
  const phone = phoneProp || contactSettings?.phone || "";
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const brandName = settings?.brand_name || "Kathmandu National";
  const brandSubtitle = settings?.brand_subtitle || "Medical College & Teaching Hospital";
  const bookLabel = settings?.book_btn_label || "Book Appointment";

  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlay]);

  const onHero = overlay && !scrolled;
  const positionClass = overlay ? "fixed top-0 left-0 right-0" : "sticky top-0";
  const bgClass = onHero
    ? "border-transparent bg-transparent backdrop-blur-0"
    : "border-border/60 bg-background/85 backdrop-blur-xl shadow-soft";
  const brandColor = onHero ? "text-white" : "text-primary";
  const brandSubColor = onHero ? "text-white/70" : "text-muted-foreground";
  const navColor = onHero
    ? "text-white/90 hover:text-gold"
    : "text-foreground/80 hover:text-primary";
  const activeColor = onHero ? "text-gold" : "text-primary";

  return (
    <header className={`${positionClass} z-40 border-b transition-colors duration-300 ${bgClass}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-3">
          <img
            src={knmcLogo}
            alt="KNMC crest"
            className={`h-12 w-12 shrink-0 object-contain transition-all ${
              onHero ? "rounded-full bg-white p-1 shadow-md ring-1 ring-white/40" : ""
            }`}
          />
          <div className="leading-tight">
            <div className={`font-display text-lg font-semibold ${brandColor}`}>{brandName}</div>
            <div className={`text-[10px] uppercase tracking-[0.18em] ${brandSubColor}`}>
              {brandSubtitle}
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {nav.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`text-sm font-medium transition-colors ${navColor} [&.active]:${activeColor}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={`tel:${phone}`}
            className={
              onHero
                ? "flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/20"
                : "flex items-center gap-2 rounded-full border border-emergency/30 bg-emergency/5 px-3 py-1.5 text-sm font-semibold text-emergency transition-all hover:-translate-y-0.5 hover:bg-emergency/10 hover:shadow-soft"
            }
          >
            <Phone className="h-4 w-4" />
          </a>
          <Link to="/doctors">
            <Button
              variant="default"
              className={
                onHero
                  ? "rounded-full bg-gold text-gold-foreground transition-all hover:bg-gold/90 hover:shadow-glow"
                  : "rounded-full bg-primary transition-all hover:bg-secondary hover:shadow-glow"
              }
            >
              {bookLabel}
            </Button>
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button
            onClick={() => setOpen(!open)}
            className={`rounded-md p-2 ${onHero ? "text-white" : ""}`}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background lg:hidden">
          <div className="space-y-1 px-6 py-4">
            {nav.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block py-2 text-sm font-medium"
              >
                {item.label}
              </Link>
            ))}
            <Link to="/doctors" onClick={() => setOpen(false)}>
              <Button className="mt-2 w-full rounded-full bg-primary">{bookLabel}</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
